import { useLang } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { useLayoutEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  GraduationCap,
  Calendar,
  ClipboardList,
  CheckSquare,
  Award,
  FolderOpen,
  Receipt,
  Megaphone,
  BarChart3,
  Settings as SettingsIcon,
  LogOut,
  X,
  User as UserIcon,
  BookOpen,
  Heart,
} from "lucide-react";

const ROLE_TITLES = {
  admin: "role_admin",
  educator: "role_educator",
  student: "role_student",
  parent: "role_parent",
};

const ROLE_COLORS = {
  admin: "var(--role-management)",
  educator: "var(--role-educator)",
  student: "var(--role-student)",
  parent: "var(--role-parent)",
};

export function getNav(role, t) {
  if (role === "admin")
    return [
      { section: t.overview, items: [
        { label: t.dashboard, to: "/dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
        { label: t.reports, to: "/reports", icon: BarChart3, testid: "nav-reports" },
      ]},
      { section: t.people, items: [
        { label: t.students, to: "/students", icon: Users, testid: "nav-students" },
        { label: t.educators, to: "/educators", icon: GraduationCap, testid: "nav-educators" },
        { label: t.parents, to: "/parents", icon: Heart, testid: "nav-parents" },
      ]},
      { section: t.academics, items: [
        { label: t.classes, to: "/classes", icon: Calendar, testid: "nav-classes" },
        { label: t.enrollment, to: "/enrollment", icon: ClipboardList, testid: "nav-enrollment" },
        { label: t.attendance, to: "/attendance", icon: CheckSquare, testid: "nav-attendance" },
        { label: t.grades, to: "/grades", icon: Award, testid: "nav-grades" },
        { label: t.materials, to: "/materials", icon: FolderOpen, testid: "nav-materials" },
      ]},
      { section: t.finance, items: [
        { label: t.invoices, to: "/invoices", icon: Receipt, testid: "nav-invoices" },
      ]},
      { section: t.system, items: [
        { label: t.announcements, to: "/announcements", icon: Megaphone, testid: "nav-announcements" },
      ]},
    ];
  if (role === "educator")
    return [
      { section: t.overview, items: [
        { label: t.myDashboard, to: "/dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
      ]},
      { section: t.academics, items: [
        { label: t.myClasses, to: "/classes", icon: BookOpen, testid: "nav-myclasses" },
        { label: t.markAttendance, to: "/attendance", icon: CheckSquare, testid: "nav-attendance" },
        { label: t.enterGrades, to: "/grades", icon: Award, testid: "nav-grades" },
        { label: t.materials, to: "/materials", icon: FolderOpen, testid: "nav-materials" },
      ]},
      { section: t.system, items: [
        { label: t.announcements, to: "/announcements", icon: Megaphone, testid: "nav-announcements" },
      ]},
    ];
  if (role === "student")
    return [
      { section: t.overview, items: [
        { label: t.myDashboard, to: "/dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
      ]},
      { section: t.academics, items: [
        { label: t.mySchedule, to: "/classes", icon: Calendar, testid: "nav-myclasses" },
        { label: t.myResults, to: "/results", icon: Award, testid: "nav-results" },
        { label: t.materials, to: "/materials", icon: FolderOpen, testid: "nav-materials" },
        { label: t.attendance, to: "/attendance", icon: CheckSquare, testid: "nav-attendance" },
      ]},
      { section: t.system, items: [
        { label: t.announcements, to: "/announcements", icon: Megaphone, testid: "nav-announcements" },
      ]},
    ];
  if (role === "parent")
    return [
      { section: t.overview, items: [
        { label: t.myDashboard, to: "/dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
      ]},
      { section: t.academics, items: [
        { label: t.myChildren, to: "/students", icon: Users, testid: "nav-mychildren" },
        { label: t.results, to: "/results", icon: Award, testid: "nav-results" },
        { label: t.attendance, to: "/attendance", icon: CheckSquare, testid: "nav-attendance" },
        { label: t.classes, to: "/classes", icon: Calendar, testid: "nav-classes" },
      ]},
      { section: t.finance, items: [
        { label: t.fees, to: "/invoices", icon: Receipt, testid: "nav-invoices" },
      ]},
      { section: t.system, items: [
        { label: t.announcements, to: "/announcements", icon: Megaphone, testid: "nav-announcements" },
      ]},
    ];
  return [];
}

export default function Sidebar({ open = false, onClose }) {
  const { t, lang } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const navRef = useRef(null);
  const [ind, setInd] = useState(null);

  // Satu penanda aktif yang meluncur ke menu terpilih (bukan latar per-menu).
  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return undefined;
    const place = () => {
      const a = nav.querySelector('a[aria-current="page"]');
      setInd(a ? { y: a.offsetTop, h: a.offsetHeight } : null);
    };
    place();
    window.addEventListener("resize", place);
    const fonts = document.fonts?.ready?.then(place);
    return () => {
      window.removeEventListener("resize", place);
      void fonts;
    };
  }, [pathname, lang, user, open]);

  if (!user) return null;
  const settingsItem = { label: t.settings, to: "/settings", icon: SettingsIcon, testid: "nav-settings" };
  const sections = getNav(user.role, t).map((sec, i, all) =>
    i === all.length - 1 ? { ...sec, items: [...sec.items, settingsItem] } : sec
  );
  const roleColor = ROLE_COLORS[user.role];
  const roleLabel = t[ROLE_TITLES[user.role]];

  const initials = (user.full_name || "U")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-slate-950/50 transition-opacity md:hidden ${
          open ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={`sidebar-glass fixed inset-y-0 left-0 z-50 flex w-64 max-w-[82vw] flex-col transition-transform duration-200 md:inset-y-6 md:left-6 md:w-[212px] md:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="sidebar"
      >
      <div className="px-5 pb-5 pt-7">
        <div className="flex items-center gap-3 relative z-10">
          <div
            className="clay-orb relative h-11 w-11 text-lg font-extrabold font-heading"
            style={{ "--t": "var(--pri)" }}
          >
            E
            <span className="absolute -right-1 -top-1 h-3 w-3 rounded-full border-2 border-white/70 bg-warn" />
          </div>
          <div className="min-w-0">
            <div className="text-lg font-extrabold leading-tight tracking-tight text-ink font-heading">{t.appName}</div>
            <div className="mt-1 text-xs font-semibold uppercase leading-tight tracking-[0.16em] text-mut">Learning OS</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto rounded-lg p-2 text-mut transition hover:bg-[color:var(--side-hov)] hover:text-ink md:hidden"
            aria-label={t.closeNav}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <nav className="relative flex-1 overflow-y-auto px-3 py-1" ref={navRef} aria-label={t.navMain}>
        {ind && <span className="ni-ind" style={{ transform: `translateY(${ind.y}px)`, height: ind.h }} aria-hidden="true" />}
        {sections.map((sec) => (
          <div key={`sec-${sec.section}`} className="mb-3">
            <div className="mb-1.5 px-3 text-xs font-bold uppercase tracking-[0.14em] text-mut font-sans">
              {sec.section}
            </div>
            {sec.items.map((it) => {
              const Icon = it.icon;
              return (
                <NavLink
                  key={it.to + it.label}
                  to={it.to}
                  data-testid={it.testid}
                  onClick={onClose}
                  className={({ isActive }) =>
                    `ni relative z-10 mb-0.5 flex items-center gap-3 rounded-full px-3.5 py-2.5 text-[13.5px] font-semibold transition-colors ${
                      isActive ? "text-[color:var(--pri-ink)]" : "text-mut hover:bg-[color:var(--side-hov)] hover:text-ink"
                    }`
                  }
                >
                  <Icon className="w-4 h-4" strokeWidth={2.2} />
                  <span>{it.label}</span>
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="m-3 rounded-[22px] bg-slate-50 p-2">
        <div className="flex items-center gap-2.5 px-1 py-1">
          <div
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white font-heading"
            style={{ background: `linear-gradient(135deg, ${roleColor}, var(--info))` }}
          >
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <div className="truncate text-xs font-bold text-ink font-heading">{user.full_name}</div>
            <div className="text-xs font-bold uppercase tracking-wider text-mut">{roleLabel}</div>
          </div>
          <button
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
            className="rounded-lg p-2 text-mut transition hover:bg-[color:var(--side-hov)] hover:text-ink"
            title={t.logout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
      </aside>
    </>
  );
}
